import { zonedDate } from '../../../utils/date-time';
// src/modules/assinaturas/estabelecimento/AssinaturasForm.tsx
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useParamsContext } from '../../../contexts/ParamsContext';
import { AssinaturasService } from './Service';
import { UserService } from '../../configuracoes/usuario/Service';
import { useAppContext } from '../../../contexts/AppContext';
import { useAssinaturasContext } from './Context';
import { useEstabsContext } from '../../../contexts/EstabsContext';
import MultiSelect from '../../../components/inputs/MultiSelect';
import { baseURL } from '../../../AxiosConfig';
import { PlanosService } from '../../planos/estabelecimento/Service';
import PriceInput from '../../../components/inputs/Price';
import { SettingsRow, SettingsSection } from '../../configuracoes/estabelecimento/Form/SettingsGroup';
import { normalizeSearch } from '../../recorrencias/helpers';
import { formatServiceRecurrence } from '../../planos/cliente/planosUtils';
import './Assinaturas.css';
import { Check } from 'lucide-react';

const assinaturasService = new AssinaturasService();
const userService = new UserService();
const planosService = new PlanosService();

type PlanoOption = { label: string; value: string; raw?: any };
type ClientOption = {
    label: string;
    value: string;
    email?: string;
    phoneNumber?: string;
};
type BillingType = 'RECORRENTE' | 'UNICO';

type PlanPeriodicity = 'single' | 'weekly' | 'biweekly' | 'monthly' | 'bimonthly' | 'quarterly' | 'biannual' | 'yearly';

const periodicityLabels: Partial<Record<PlanPeriodicity, string>> = {
    single: 'Único',
    weekly: 'Semanal',
    biweekly: 'Quinzenal',
    monthly: 'Mensal',
    bimonthly: 'Bimestral',
    quarterly: 'Trimestral',
    biannual: 'Semestral',
    yearly: 'Anual',
};

const brDate = (ymd?: string | Date) => {
    if (!ymd) return '-';
    const d = typeof ymd === 'string' ? new Date(ymd + 'T00:00:00') : new Date(ymd);
    return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('pt-BR');
};

const toBRL = (num?: number) =>
    typeof num === 'number' && !Number.isNaN(num)
        ? num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        : '-';

const parsePriceInput = (value?: string | number) => {
    if (value === undefined || value === null) return Number.NaN;
    const raw = String(value).trim();
    if (!raw) return Number.NaN;

    if (raw.includes(',')) {
        return Number(raw.replace(/\./g, '').replace(',', '.'));
    }

    return Number(raw);
};

const parsePercentInput = (value?: string | number) => {
    if (value === undefined || value === null) return Number.NaN;
    const normalized = String(value).replace(',', '.').trim();
    if (!normalized) return Number.NaN;
    return Number(normalized);
};

const parseBooleanFlag = (value: unknown) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    return false;
};

const getInitials = (name?: string) => {
    const parts = String(name || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2);

    if (!parts.length) return '?';
    return parts.map((part) => part[0]?.toUpperCase()).join('');
};

const getClientContactText = (option: ClientOption) => {
    const contact = [option?.email, option?.phoneNumber]
        .map((item) => String(item || '').trim())
        .filter(Boolean);

    return contact.join(' • ');
};

const ClientOptionLabel = ({
    option,
    context,
    selected,
}: {
    option: ClientOption;
    context: 'menu' | 'value';
    selected: boolean;
}) => {
    const [imageError, setImageError] = useState(false);
    const initials = getInitials(option?.label);
    const imageUrl = `${baseURL}/users/profileImage/${option?.value}`;
    const contactText = getClientContactText(option);

    if (context === 'value') {
        return (
            <div className="flex min-w-0 items-center gap-2">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/15 text-[10px] font-bold text-primary">
                    {!imageError ? (
                        <img
                            src={imageUrl}
                            alt=""
                            className="h-full w-full object-cover"
                            onError={() => setImageError(true)}
                        />
                    ) : (
                        initials
                    )}
                </div>
                <span className="truncate">{option.label}</span>
            </div>
        );
    }

    return (
        <div
            className={`flex min-w-0 items-center gap-3 rounded-xl px-2.5 py-2 transition-all duration-150 hover:bg-primary/10 ${
                selected ? 'bg-primary/10' : ''
            }`}
        >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/15 text-xs font-bold text-primary ring-1 ring-primary/20">
                {!imageError ? (
                    <img
                        src={imageUrl}
                        alt=""
                        className="h-full w-full object-cover"
                        onError={() => setImageError(true)}
                    />
                ) : (
                    initials
                )}
            </div>
            <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-base-content">
                    {option.label}
                </div>
                <div className="truncate text-[11px] font-medium text-base-content/50">
                    {contactText || 'Sem email ou telefone'}
                </div>
            </div>
            {selected && <Check className="text-xs text-primary" aria-hidden="true" />}
        </div>
    );
};

// Soma N ciclos em cima de uma data base (espelha addCycles do backend)
const addCyclesYMD = (baseYMD: string, periodicity?: PlanPeriodicity, cycles = 1): string => {
    if (!baseYMD || !periodicity || cycles <= 0) return '';
    const d = new Date(baseYMD + 'T00:00:00Z');
    if (Number.isNaN(d.getTime())) return '';

    switch (periodicity) {
        case 'weekly':
            d.setUTCDate(d.getUTCDate() + 7 * cycles);
            break;
        case 'biweekly':
            d.setUTCDate(d.getUTCDate() + 14 * cycles);
            break;
        case 'monthly':
            d.setUTCMonth(d.getUTCMonth() + cycles);
            break;
        case 'bimonthly':
            d.setUTCMonth(d.getUTCMonth() + 2 * cycles);
            break;
        case 'quarterly':
            d.setUTCMonth(d.getUTCMonth() + 3 * cycles);
            break;
        case 'biannual':
            d.setUTCMonth(d.getUTCMonth() + 6 * cycles);
            break;
        case 'yearly':
            d.setUTCFullYear(d.getUTCFullYear() + cycles);
            break;
        case 'single':
        default:
            // não altera a data
            break;
    }

    return d.toISOString().slice(0, 10);
};

// Espelha calcTotalCyclesFromConfig do backend
const calcTotalCyclesFromConfigFront = (opts: {
    periodicity?: PlanPeriodicity;
    indeterminateQuantity?: boolean;
    quantity?: number;
    billingType: BillingType;
}): number => {
    const { periodicity, indeterminateQuantity, quantity, billingType } = opts;

    if (billingType === 'UNICO' || periodicity === 'single') {
        return 1;
    }

    if (indeterminateQuantity) {
        return 0;
    }

    if (typeof quantity === 'number' && quantity > 0) {
        return quantity;
    }

    return 0;
};

const computeEndDateYMD = (params: {
    startDateYMD: string;
    periodicity?: PlanPeriodicity;
    quantity?: number;
    indeterminateQuantity?: boolean;
    billingType: BillingType;
}): string => {
    const { startDateYMD, periodicity, quantity, indeterminateQuantity, billingType } = params;
    if (!startDateYMD) return '';

    const totalCycles = calcTotalCyclesFromConfigFront({
        periodicity,
        indeterminateQuantity,
        quantity,
        billingType,
    });

    if (!periodicity || totalCycles <= 0) return '';

    return addCyclesYMD(startDateYMD, periodicity, totalCycles);
};

const computeNextBillingYMD = (params: {
    startDateYMD: string;
    periodicity?: PlanPeriodicity;
    billingType: BillingType;
}): string => {
    const { startDateYMD, periodicity, billingType } = params;
    if (!startDateYMD) return '';

    // single não tem próxima cobrança automática
    if (!periodicity || periodicity === 'single') return '';

    // pagamento único não tem recorrência
    if (billingType === 'UNICO') return '';

    return addCyclesYMD(startDateYMD, periodicity, 1);
};

export default function AssinaturasForm() {
    const { clear, setParams } = useParamsContext();
    const { reloadData } = useAssinaturasContext();
    const { estabSession } = useEstabsContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const allowAgendamentoDiscountField = parseBooleanFlag(
        estabSession?.enablePlanoAgendamentoDiscountField,
    );

    const [disabledButton, setDisabledButton] = useState(false);

    // Plano
    const [planoId, setPlanoId] = useState('');
    const [planosOptions, setPlanosOptions] = useState<PlanoOption[]>([]);
    const [planoSelecionado, setPlanoSelecionado] = useState<any | null>(null);

    // Cliente
    const [clientId, setClientId] = useState<string>('');
    const [clients, setClients] = useState<ClientOption[]>([]);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [clientInputValue, setClientInputValue] = useState('');

    // Outros
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>(''); // 🔹 NOVO: data de término para pagamento único

    // Tipo de cobrança + flags de “toque manual”
    const [billingType, setBillingType] = useState<BillingType>('RECORRENTE');
    const [billingTouched, setBillingTouched] = useState(false);
    const isUnico = billingType === 'UNICO';

    // Config. do plano para esta assinatura
    const [periodicityValue, setPeriodicityValue] = useState<PlanPeriodicity | ''>('');
    const [quantityStr, setQuantityStr] = useState<string>('');
    const [indeterminate, setIndeterminate] = useState<boolean>(false);

    // Overrides editáveis
    const [priceStr, setPriceStr] = useState<string>(''); // preço desta assinatura
    const [priceTouched, setPriceTouched] = useState(false);
    const [agendamentoDescontoPercentualStr, setAgendamentoDescontoPercentualStr] =
        useState<string>('0');
    const [agendamentoDescontoTouched, setAgendamentoDescontoTouched] =
        useState(false);

    // Previews
    const [computedNext, setComputedNext] = useState<string>(''); // próxima cobrança
    const [computedEnd, setComputedEnd] = useState<string>(''); // término

    const isPlanChosen = Boolean(planoSelecionado);

    // Derivados da config atual (plano + overrides)
    const currentPeriodicity = (periodicityValue || (planoSelecionado?.periodicity as PlanPeriodicity | undefined)) as
        | PlanPeriodicity
        | undefined;

    const isSinglePlan = currentPeriodicity === 'single';
    const isSingleLike = isUnico || isSinglePlan;
    const periodicityLabel = (currentPeriodicity && periodicityLabels[currentPeriodicity]) || undefined;

    const quantityValue = quantityStr && !Number.isNaN(Number(quantityStr)) ? Number(quantityStr) : undefined;
    const planQuantity =
        typeof planoSelecionado?.quantity === 'number' ? planoSelecionado.quantity : undefined;
    const effectiveQuantity = !indeterminate
        ? typeof quantityValue === 'number'
            ? quantityValue
            : planQuantity
        : undefined;
    const quantityHelperText = indeterminate
        ? 'Indeterminada: cobranças até cancelamento.'
        : typeof quantityValue === 'number'
          ? 'Quantidade personalizada para esta assinatura.'
          : typeof planQuantity === 'number'
            ? `Se vazio, usa a quantidade do plano (${planQuantity}).`
            : 'Se vazio, o plano segue sem limite definido.';

    // Carrega planos
    useEffect(() => {
        (async () => {
            try {
                const { data } = await planosService.findAll();
                const options: PlanoOption[] = (data || [])
                    .filter((p: any) => p.status === 1)
                    .map((p: any) => ({
                    label: p.name,
                    value: p._id,
                    raw: {
                        _id: p._id,
                        name: p.name,
                        price: p.price,
                        periodicity: p.periodicity, // PlanPeriodicity
                        quantity: p.quantity, // number
                        indeterminateQuantity: p.indeterminateQuantity, // boolean
                        billingType: p.billingType, // opcional
                        agendamentoDescontoPercentual:
                            p.agendamentoDescontoPercentual,
                        services: p.services,
                        singleValidityValue: p.singleValidityValue,
                        singleValidityUnit: p.singleValidityUnit,
                    },
                }));
                setPlanosOptions(options);
            } catch (err) {
                console.error('Erro ao buscar planos:', err);
            }
        })();
    }, []);

    // Calcula data de término para planos de pagamento único,
    // baseado em singleValidityValue / singleValidityUnit do plano.
    const computeSingleEndDateFromPlan = (startDateYMD: string, plan: any): string => {
        if (!startDateYMD || !plan) return '';

        const value = Number(plan.singleValidityValue || 0);
        const unitRaw = String(plan.singleValidityUnit || '').toLowerCase();

        if (!value || !['day', 'days', 'month', 'months'].includes(unitRaw)) {
            return '';
        }

        const d = new Date(startDateYMD + 'T00:00:00Z');
        if (Number.isNaN(d.getTime())) return '';

        if (unitRaw.startsWith('day')) {
            d.setUTCDate(d.getUTCDate() + value);
        } else {
            d.setUTCMonth(d.getUTCMonth() + value);
        }

        return d.toISOString().slice(0, 10);
    };

    // Quando for plano de pagamento único com validade configurada,
    // calcula automaticamente a data de término ao selecionar o plano
    // ou alterar a data de início.
    useEffect(() => {
        if (!planoSelecionado || !startDate) return;

        const isSingleConfig = currentPeriodicity === 'single' || billingType === 'UNICO';

        if (!isSingleConfig) return;

        const autoEnd = computeSingleEndDateFromPlan(startDate, planoSelecionado);

        if (autoEnd) {
            setEndDate(autoEnd);
        }
    }, [planoSelecionado, startDate, currentPeriodicity, billingType]);

    // Carrega clientes
    useEffect(() => {
        (async () => {
            try {
                setClientsLoading(true);
                const { data } = await userService.getClients();
                setClients(
                    (data || []).map((c: any) => ({
                        label: c.displayName || c.email || c.phoneNumber || 'Sem nome',
                        value: c.uid,
                        email: c.email,
                        phoneNumber: c.phoneNumber,
                    }))
                );
            } catch (err) {
                console.error('Erro ao buscar clientes:', err);
            } finally {
                setClientsLoading(false);
            }
        })();
    }, []);

    // Data inicial padrão
    useEffect(() => {
        if (!startDate) setStartDate(zonedDate(new Date(), estabSession?.timezone).format('YYYY-MM-DD'));
    }, [startDate, estabSession?.timezone]);

    // Sempre que deixar de ser pagamento único, limpa data de término
    useEffect(() => {
        if (!isSingleLike && endDate) {
            setEndDate('');
        }
    }, [isSingleLike, endDate]);

    // Atualiza planoSelecionado
    useEffect(() => {
        if (!planoId || !planosOptions.length) {
            setPlanoSelecionado(null);
            return;
        }
        const found = planosOptions.find((p) => p.value === planoId);
        setPlanoSelecionado(found?.raw || null);
    }, [planoId, planosOptions]);

    // Ao trocar de plano, preenche os campos com os valores dele. Roda so na troca de plano:
    // editar o valor ou o tipo de cobranca nao pode desfazer outros ajustes desta assinatura.
    useEffect(() => {
        if (!planoSelecionado) return;

        const periodicity = planoSelecionado?.periodicity as PlanPeriodicity | undefined;

        // billingType padrão
        if (!billingTouched) {
            const planBT: BillingType =
                (planoSelecionado?.billingType as BillingType) || (periodicity === 'single' ? 'UNICO' : 'RECORRENTE');

            setBillingType(planBT);
        }

        // preço padrão
        if (!priceTouched) {
            const p = Number(planoSelecionado?.price ?? 0);
            setPriceStr(p ? String(p) : '');
        }
        if (!agendamentoDescontoTouched) {
            const planoDiscount = parsePercentInput(
                planoSelecionado?.agendamentoDescontoPercentual,
            );
            setAgendamentoDescontoPercentualStr(
                Number.isFinite(planoDiscount)
                    ? String(Math.round(planoDiscount * 100) / 100)
                    : '0',
            );
        }

        // configs do plano para esta assinatura
        setPeriodicityValue(periodicity || '');
        const q = typeof planoSelecionado?.quantity === 'number' ? planoSelecionado.quantity : undefined;
        setQuantityStr(q ? String(q) : '');
        setIndeterminate(Boolean(planoSelecionado?.indeterminateQuantity));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [planoSelecionado]);

    // Garante que periodicidade "single" fique sempre como UNICO
    useEffect(() => {
        const periodicity = currentPeriodicity;
        if (periodicity === 'single' && billingType !== 'UNICO') {
            setBillingType('UNICO');
        }
    }, [currentPeriodicity, billingType]);

    // Previews (próxima cobrança / término previsto) alinhados com o backend
    useEffect(() => {
        if (!startDate || !planoSelecionado) {
            setComputedNext('');
            setComputedEnd('');
            return;
        }

        const periodicity = currentPeriodicity;
        const indeterminateQuantity = indeterminate;

        // Próxima cobrança (para UNICO já fica vazio pela função)
        const nextYMD = computeNextBillingYMD({
            startDateYMD: startDate,
            periodicity,
            billingType,
        });
        setComputedNext(nextYMD);

        // Para pagamento único, usamos a data de término informada no formulário
        if (isSingleLike) {
            setComputedEnd(endDate || '');
            return;
        }

        // Para recorrentes, calculamos normalmente pelos ciclos
        const endYMD = computeEndDateYMD({
            startDateYMD: startDate,
            periodicity,
            quantity: effectiveQuantity,
            indeterminateQuantity,
            billingType,
        });

        setComputedEnd(endYMD);
    }, [
        planoSelecionado,
        startDate,
        billingType,
        currentPeriodicity,
        effectiveQuantity,
        indeterminate,
        endDate,
        isSingleLike,
    ]);

    // Criação rápida de cliente
    const handleCreateClient = async (inputValue: string) => {
        try {
            setClientsLoading(true);
            const { data } = await userService.createClient({ name: inputValue });
            const newOption: ClientOption = {
                value: data.uid,
                label: inputValue,
                email: data.email,
                phoneNumber: data.phoneNumber,
            };
            setClients((prev) => [newOption, ...prev]);
            setClientId(data.uid);
        } catch (error) {
            console.error('Erro ao criar cliente:', error);
            errorSnackbar('Erro ao criar cliente.');
        } finally {
            setClientsLoading(false);
        }
    };

    // Filtro cliente
    let filteredClients = clients
        .filter((c) => normalizeSearch(c.label).includes(normalizeSearch(clientInputValue)))
        .slice(0, 10);
    if (clientId && !filteredClients.some((c) => c.value === clientId)) {
        const selectedClient = clients.find((c) => c.value === clientId);
        if (selectedClient) filteredClients = [selectedClient, ...filteredClients];
    }

    // Submit
    const submit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!planoId || !clientId || !startDate) {
            errorSnackbar('Preencha cliente, plano e data de início.');
            return;
        }

        // 🔹 Validação extra para pagamento único: exige data de término
        if (isSingleLike) {
            if (!endDate) {
                errorSnackbar('Para pagamento único, informe a data de término da assinatura.');
                return;
            }

            const start = new Date(startDate + 'T00:00:00');
            const end = new Date(endDate + 'T00:00:00');

            if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
                errorSnackbar('Datas inválidas. Verifique início e término.');
                return;
            }

            if (end < start) {
                errorSnackbar('A data de término não pode ser anterior à data de início.');
                return;
            }
        }

        const parsedPrice = parsePriceInput(priceStr);
        const priceNumber =
            Number.isFinite(parsedPrice) && parsedPrice > 0
                ? parsedPrice
                : Number(planoSelecionado?.price || 0);
        const parsedAgendamentoDesconto = parsePercentInput(
            agendamentoDescontoPercentualStr,
        );

        if (
            allowAgendamentoDiscountField &&
            (
                !Number.isFinite(parsedAgendamentoDesconto) ||
                parsedAgendamentoDesconto < 0 ||
                parsedAgendamentoDesconto > 100
            )
        ) {
            errorSnackbar('Informe um desconto entre 0 e 100% para o agendamento.');
            return;
        }

        const periodicityToSend = currentPeriodicity;
        const quantityToSend =
            !indeterminate && typeof quantityValue === 'number' && quantityValue > 0 ? quantityValue : undefined;

        setDisabledButton(true);
        try {
            const assinatura: any = {
                planoId,
                clienteId: clientId,
                startAt: startDate,
                billingType, // 'RECORRENTE' | 'UNICO'
                price: priceNumber,
                periodicity: periodicityToSend,
                quantity: quantityToSend,
                indeterminateQuantity: indeterminate,
            };

            if (allowAgendamentoDiscountField) {
                assinatura.agendamentoDescontoPercentual =
                    Math.round(parsedAgendamentoDesconto * 100) / 100;
            }

            // 🔹 Se for pagamento único, já mandamos o endAt para o backend (se ele aceitar)
            if (billingType === 'UNICO' && endDate) {
                assinatura.endAt = endDate;
            }

            const { data } = await assinaturasService.create(assinatura);
            successSnackbar('Assinatura criada com sucesso!');
            reloadData();
            // Vai direto para a assinatura: o proximo passo costuma ser criar a recorrencia.
            if (data?._id) {
                setParams({ action: 'periodos', id: data._id });
            } else {
                clear();
            }
        } catch (err: any) {
            console.error(err);
            const message = err?.response?.data?.message;
            errorSnackbar(
                (Array.isArray(message) ? message.join(', ') : message) ||
                    'Ocorreu um erro ao salvar a assinatura.',
            );
        } finally {
            setDisabledButton(false);
        }
    };

    const previewValue = Number.isFinite(parsePriceInput(priceStr))
        ? parsePriceInput(priceStr)
        : typeof planoSelecionado?.price === 'number'
          ? planoSelecionado.price
          : undefined;
    const pricePreview = toBRL(
        typeof previewValue === 'number' && Number.isFinite(previewValue)
            ? previewValue
            : undefined,
    );

    const endLabel = computedEnd
        ? brDate(computedEnd)
        : indeterminate && !isSingleLike
          ? 'Sem previsão (quantidade indeterminada)'
          : !isSingleLike && !effectiveQuantity
            ? 'Sem previsão (quantidade não definida)'
            : '—';

    return (
        <form onSubmit={submit} className="estab-settings ass-page px-2">
            <div className="ass-form-grid">
                <div className="ass-form-main">
                    <SettingsSection title="Cliente e plano">
                        <SettingsRow stacked>
                            <div className="ass-client-select">
                                <MultiSelect
                                    options={filteredClients}
                                    loading={clientsLoading}
                                    disabled={clientsLoading}
                                    value={clientId}
                                    onChange={(selected) => setClientId(selected as string)}
                                    onCreateOption={handleCreateClient}
                                    placeholder="Buscar ou criar cliente"
                                    formatOptionLabel={(option: ClientOption, meta: any) => (
                                        <ClientOptionLabel
                                            option={option}
                                            context={meta.context}
                                            selected={clientId === option.value}
                                        />
                                    )}
                                    name="cliente"
                                    multiple={false}
                                    menuOverlay
                                    onInputChange={(value: any) => setClientInputValue(value)}
                                />
                            </div>
                        </SettingsRow>
                        <SettingsRow label="Plano" htmlFor="assinatura-plano">
                            <select
                                id="assinatura-plano"
                                className="select estab-field"
                                value={planoId}
                                onChange={(e) => setPlanoId(e.target.value)}
                                required
                            >
                                <option value="">Selecione um plano</option>
                                {planosOptions.map(({ label, value }) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </SettingsRow>
                        <SettingsRow label="Início" htmlFor="assinatura-inicio">
                            <input
                                id="assinatura-inicio"
                                type="date"
                                className="input estab-field"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                required
                            />
                        </SettingsRow>
                        {isSingleLike && (
                            <SettingsRow
                                label="Término"
                                htmlFor="assinatura-termino"
                                description="Até quando o cliente pode usar o plano."
                            >
                                <input
                                    id="assinatura-termino"
                                    type="date"
                                    className="input estab-field"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    required
                                />
                            </SettingsRow>
                        )}
                    </SettingsSection>

                    {/* Condições visíveis só após escolher o plano */}
                    {isPlanChosen && (
                        <SettingsSection
                            title="Condições desta assinatura"
                            footer={
                                isSingleLike
                                    ? 'Pagamento único não gera novas cobranças nem renovação automática.'
                                    : 'Alterações aqui valem só para esta assinatura, não para o plano.'
                            }
                        >
                            <SettingsRow label="Valor">
                                <PriceInput
                                    value={priceStr}
                                    onChange={(e: any) => {
                                        setPriceStr(String(e.target.value ?? ''));
                                        setPriceTouched(true);
                                    }}
                                    placeholder="R$ 0,00"
                                    label=""
                                    className="estab-field w-full"
                                />
                            </SettingsRow>

                            <SettingsRow stacked>
                                <div className="sf-segmented" role="group" aria-label="Tipo de cobrança">
                                    <button
                                        type="button"
                                        aria-pressed={billingType === 'RECORRENTE'}
                                        disabled={isSinglePlan}
                                        onClick={() => {
                                            setBillingType('RECORRENTE');
                                            setBillingTouched(true);
                                        }}
                                    >
                                        Recorrente
                                    </button>
                                    <button
                                        type="button"
                                        aria-pressed={billingType === 'UNICO'}
                                        onClick={() => {
                                            setBillingType('UNICO');
                                            setBillingTouched(true);
                                        }}
                                    >
                                        Pagamento único
                                    </button>
                                </div>
                            </SettingsRow>

                            {allowAgendamentoDiscountField && (
                                <SettingsRow
                                    label="Desconto no agendamento"
                                    htmlFor="assinatura-desconto"
                                    description="Em %: 100 zera o item coberto; 0 não aplica desconto."
                                >
                                    <input
                                        id="assinatura-desconto"
                                        type="number"
                                        className="input estab-field"
                                        min={0}
                                        max={100}
                                        step="0.01"
                                        value={agendamentoDescontoPercentualStr}
                                        onChange={(e) => {
                                            setAgendamentoDescontoPercentualStr(e.target.value);
                                            setAgendamentoDescontoTouched(true);
                                        }}
                                        placeholder="0"
                                    />
                                </SettingsRow>
                            )}

                            {/* Configuração do plano: só aparece se NÃO for pagamento único */}
                            {!isSingleLike && (
                                <>
                                    <SettingsRow label="Periodicidade" htmlFor="assinatura-periodicidade">
                                        <select
                                            id="assinatura-periodicidade"
                                            className="select estab-field"
                                            value={currentPeriodicity || ''}
                                            onChange={(e) =>
                                                setPeriodicityValue(
                                                    e.target.value ? (e.target.value as PlanPeriodicity) : ''
                                                )
                                            }
                                        >
                                            {(
                                                [
                                                    'single',
                                                    'weekly',
                                                    'biweekly',
                                                    'monthly',
                                                    'bimonthly',
                                                    'quarterly',
                                                    'biannual',
                                                    'yearly',
                                                ] as PlanPeriodicity[]
                                            ).map((p) => (
                                                <option key={p} value={p}>
                                                    {periodicityLabels[p]}
                                                </option>
                                            ))}
                                        </select>
                                    </SettingsRow>
                                    <SettingsRow
                                        label="Até cancelar"
                                        htmlFor="assinatura-indeterminada"
                                        description="Cobra até a assinatura ser cancelada."
                                        inline
                                    >
                                        <input
                                            id="assinatura-indeterminada"
                                            type="checkbox"
                                            className="toggle toggle-success"
                                            checked={indeterminate}
                                            onChange={(e) => setIndeterminate(e.target.checked)}
                                        />
                                    </SettingsRow>
                                    {!indeterminate && (
                                        <SettingsRow
                                            label="Cobranças"
                                            htmlFor="assinatura-quantidade"
                                            description={quantityHelperText}
                                        >
                                            <input
                                                id="assinatura-quantidade"
                                                type="number"
                                                className="input estab-field"
                                                min={1}
                                                value={quantityStr}
                                                onChange={(e) => setQuantityStr(e.target.value)}
                                                placeholder="Ex.: 12"
                                            />
                                        </SettingsRow>
                                    )}
                                </>
                            )}
                        </SettingsSection>
                    )}
                </div>

                {/* Resumo do que será criado */}
                <aside className="ass-form-aside">
                    <h3 className="sf-group-title">Resumo</h3>
                    <div className="sf-group">
                        <div className="sf-info-row">
                            <span>Cliente</span>
                            <span>{clients.find((c) => c.value === clientId)?.label || '—'}</span>
                        </div>
                        <div className="sf-info-row">
                            <span>Plano</span>
                            <span>{planoSelecionado?.name || '—'}</span>
                        </div>
                        <div className="sf-info-row">
                            <span>Valor</span>
                            <span>{pricePreview}</span>
                        </div>
                        <div className="sf-info-row">
                            <span>Cobrança</span>
                            <span>{isSingleLike ? 'Pagamento único' : periodicityLabel || 'Recorrente'}</span>
                        </div>
                        {allowAgendamentoDiscountField && (
                            <div className="sf-info-row">
                                <span>Desconto</span>
                                <span>
                                    {Number.isFinite(parsePercentInput(agendamentoDescontoPercentualStr))
                                        ? `${parsePercentInput(agendamentoDescontoPercentualStr).toLocaleString('pt-BR')}%`
                                        : '0%'}
                                </span>
                            </div>
                        )}
                        <div className="sf-info-row">
                            <span>Início</span>
                            <span>{brDate(startDate)}</span>
                        </div>
                        {!isSingleLike && (
                            <div className="sf-info-row">
                                <span>Próxima cobrança</span>
                                <span>{brDate(computedNext)}</span>
                            </div>
                        )}
                        <div className="sf-info-row">
                            <span>Término</span>
                            <span>{endLabel}</span>
                        </div>
                    </div>

                    {Array.isArray(planoSelecionado?.services) && planoSelecionado.services.length > 0 && (
                        <>
                            <h3 className="sf-group-title mt-4">Serviços incluídos</h3>
                            <div className="sf-group">
                                {planoSelecionado.services.map((s: any, idx: number) => (
                                    <div key={idx} className="sf-info-row">
                                        <span className="min-w-0 !flex-shrink">
                                            {s.serviceName || s.service?.name || s.name || 'Serviço'}
                                        </span>
                                        <span className="shrink-0">
                                            {s.quantity || 1}x {formatServiceRecurrence(s.recurrence || 'mensal')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </aside>
            </div>

            <div className="estab-savebar mt-6 gap-2">
                <button
                    type="button"
                    className="btn btn-ghost flex-1 sm:flex-none sm:min-w-[8rem]"
                    onClick={clear}
                    disabled={disabledButton}
                >
                    Cancelar
                </button>
                <button
                    className="btn btn-primary flex-1 sm:flex-none sm:min-w-[11rem]"
                    disabled={disabledButton}
                    type="submit"
                >
                    {disabledButton && <span className="loading loading-spinner loading-sm"></span>}
                    Criar assinatura
                </button>
            </div>
        </form>
    );
}
